import { Link } from "wouter";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import {
  COMPANY_FEATURES,
  CANDIDATE_FEATURES,
  COMPANY_FEATURES_BY_STAGE,
  type FeatureContent,
} from "@shared/featureContent";

function featuresFor(variant: "company" | "candidate"): FeatureContent[] {
  return variant === "company" ? COMPANY_FEATURES : CANDIDATE_FEATURES;
}

/**
 * Company "Features" mega-menu. The five hiring-mission stages sit in a left
 * rail and act as tabs; hovering or focusing a stage reveals that stage's
 * features in the right pane. Keeps the menu short and makes the stages read
 * as a journey rather than a tall stacked list.
 */
function CompanyFeaturesDropdown() {
  const stageGroups = COMPANY_FEATURES_BY_STAGE.filter((group) => group.features.length > 0);
  const [activeStageId, setActiveStageId] = useState<number>(stageGroups[0]?.stage.id ?? 1);
  const activeGroup =
    stageGroups.find((group) => group.stage.id === activeStageId) ?? stageGroups[0];

  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className="bg-transparent px-0 h-auto text-sm font-normal text-gray-600 dark:text-gray-300 hover:bg-transparent hover:text-gray-900 dark:hover:text-white focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent data-[state=open]:text-gray-900 dark:data-[state=open]:text-white"
            data-testid="nav-features-trigger"
          >
            Features
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-[680px] p-4" data-testid="nav-features-menu">
              <NavigationMenuLink asChild>
                <Link
                  href="/what-you-get"
                  className="block rounded-lg p-4 bg-accent/50 hover:bg-accent focus:bg-accent transition-colors"
                  data-testid="nav-feature-what-you-get"
                >
                  <span className="block text-sm font-semibold text-gray-900 dark:text-white">
                    What you get
                  </span>
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                    A scorecard for every shortlisted candidate.
                  </span>
                </Link>
              </NavigationMenuLink>

              <Separator className="my-4" />

              <div className="flex gap-4">
                <ul
                  className="w-60 shrink-0 flex flex-col gap-1 border-r border-gray-100 dark:border-gray-800 pr-3"
                  data-testid="nav-stage-tabs"
                >
                  {stageGroups.map((group) => {
                    const isActive = group.stage.id === activeStageId;
                    return (
                      <li key={group.stage.id}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveStageId(group.stage.id)}
                          onFocus={() => setActiveStageId(group.stage.id)}
                          aria-current={isActive ? "true" : undefined}
                          className={`w-full text-left rounded-lg px-3 py-2 transition-colors ${
                            isActive ? "bg-accent" : "hover:bg-accent/60 focus:bg-accent/60"
                          }`}
                          data-testid={`nav-stage-tab-${group.stage.id}`}
                        >
                          <span className="block text-[11px] font-semibold uppercase tracking-wide text-primary/80">
                            Stage {group.stage.id}
                          </span>
                          <span className="block text-sm font-medium text-gray-900 dark:text-white">
                            {group.stage.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <ul className="flex-1 flex flex-col gap-1" data-testid="nav-stage-features">
                  {activeGroup?.features.map((feature) => (
                    <li key={feature.slug}>
                      <NavigationMenuLink asChild>
                        <Link
                          href={feature.path}
                          className="block rounded-lg px-3 py-2.5 hover:bg-accent focus:bg-accent transition-colors"
                          data-testid={`nav-feature-${feature.slug}`}
                        >
                          <span className="block text-sm font-medium text-gray-900 dark:text-white">
                            {feature.name}
                          </span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                            {feature.navDescription}
                          </span>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </div>

              <Separator className="my-4" />

              <NavigationMenuLink asChild>
                <Link
                  href="/how-it-works"
                  className="block rounded-lg p-4 hover:bg-accent focus:bg-accent transition-colors"
                  data-testid="nav-feature-how-it-works"
                >
                  <span className="block text-sm font-semibold text-gray-900 dark:text-white">
                    How it works
                  </span>
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                    See the full hiring mission, stage by stage.
                  </span>
                </Link>
              </NavigationMenuLink>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

/** Desktop "Features" dropdown using the Radix navigation menu. */
export function FeaturesNavDropdown({ variant }: { variant: "company" | "candidate" }) {
  if (variant === "company") {
    return <CompanyFeaturesDropdown />;
  }

  const features = CANDIDATE_FEATURES;
  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className="bg-transparent px-0 h-auto text-sm font-normal text-gray-600 dark:text-gray-300 hover:bg-transparent hover:text-gray-900 dark:hover:text-white focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent data-[state=open]:text-gray-900 dark:data-[state=open]:text-white"
            data-testid="nav-features-trigger"
          >
            Features
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-[520px] p-3" data-testid="nav-features-menu">
              <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Before, during, and after you apply
              </p>
              <ul className="grid grid-cols-2 gap-1">
                {features.map((feature) => (
                  <li key={feature.slug}>
                    <NavigationMenuLink asChild>
                      <Link
                        href={feature.path}
                        className="block rounded-md p-3 hover:bg-accent focus:bg-accent transition-colors"
                        data-testid={`nav-feature-${feature.slug}`}
                      >
                        <span className="block text-sm font-medium text-gray-900 dark:text-white">
                          {feature.name}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                          {feature.navDescription}
                        </span>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                ))}
              </ul>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

/** Mobile "Features" collapsible list for the hamburger menu. */
export function FeaturesNavMobile({
  variant,
  onNavigate,
}: {
  variant: "company" | "candidate";
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const features = featuresFor(variant);
  return (
    <div>
      <button
        type="button"
        className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-300"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        data-testid="nav-features-mobile-toggle"
      >
        Features
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && variant === "company" && (
        <div className="mt-3 ml-2 flex flex-col gap-3 border-l border-gray-200 dark:border-gray-700 pl-3">
          <Link
            href="/what-you-get"
            className="block text-sm font-medium text-gray-900 dark:text-white"
            onClick={onNavigate}
            data-testid="nav-feature-mobile-what-you-get"
          >
            What you get
          </Link>
          {COMPANY_FEATURES_BY_STAGE.filter((group) => group.features.length > 0).map((group) => (
            <div key={group.stage.id} className="flex flex-col gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Stage {group.stage.id} · {group.stage.label}
              </p>
              {group.features.map((feature) => (
                <Link
                  key={feature.slug}
                  href={feature.path}
                  className="block text-sm text-gray-600 dark:text-gray-300 pl-2"
                  onClick={onNavigate}
                  data-testid={`nav-feature-mobile-${feature.slug}`}
                >
                  {feature.name}
                </Link>
              ))}
            </div>
          ))}
          <Link
            href="/how-it-works"
            className="block text-sm font-medium text-gray-900 dark:text-white"
            onClick={onNavigate}
            data-testid="nav-feature-mobile-how-it-works"
          >
            How it works
          </Link>
        </div>
      )}
      {open && variant === "candidate" && (
        <ul className="mt-3 ml-2 flex flex-col gap-3 border-l border-gray-200 dark:border-gray-700 pl-3">
          <li>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Before, during, and after you apply
            </p>
          </li>
          {features.map((feature) => (
            <li key={feature.slug}>
              <Link
                href={feature.path}
                className="block text-sm text-gray-600 dark:text-gray-300"
                onClick={onNavigate}
                data-testid={`nav-feature-mobile-${feature.slug}`}
              >
                {feature.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
